import { useState, useEffect } from 'react';
import { Question } from '@/types/questions';
import { Button } from '@/components/ui/button';
import type { UserData } from "@/lib/firebase/user";
import React from 'react';

interface StudyModeProps {
  questions: Question[];
  currentQuestionIndex: number;
  onNextQuestion: () => void;
  onPreviousQuestion: () => void;
  totalQuestions: number;
  onAnswerSubmit: (answer: string) => void;
  userData: UserData | null;
  onShowPaywall: () => void;
  hasHitPaywall: boolean;
  onComplete: (correctAnswers: number) => void;
}

const StudyMode = ({ 
  questions, 
  currentQuestionIndex,
  onNextQuestion,
  onPreviousQuestion,
  totalQuestions,
  onAnswerSubmit,
  userData: _userData,
  onShowPaywall,
  hasHitPaywall,
  onComplete
}: StudyModeProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<Record<number, string>>({});
  
  // Memoize current question to prevent unnecessary re-renders
  const currentQuestion = React.useMemo(() => 
    questions[currentQuestionIndex], 
    [questions, currentQuestionIndex]
  );

  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleOptionClick = (optionIndex: number) => {
    // Only allow selection if question hasn't been answered yet
    if (!answeredQuestions[currentQuestionIndex]) {
      const selectedOption = String.fromCharCode(65 + optionIndex);
      
      // Call parent's onAnswerSubmit first to check if we can proceed
      onAnswerSubmit(selectedOption);

      // Only check paywall for non-premium users
      if (hasHitPaywall && !_userData?.isPremium) {
        onShowPaywall();
        return;
      }

      setSelectedAnswer(selectedOption);
      setShowAnswer(true);

      // Use correctOption if available, otherwise fall back to answer
      const correctAnswer = currentQuestion.correctOption || currentQuestion.answer;
      if (selectedOption === correctAnswer) {
        setCorrectAnswers(prev => prev + 1);
      }

      setAnsweredQuestions(prev => ({
        ...prev,
        [currentQuestionIndex]: selectedOption
      }));
    }
  };

  const handleNextQuestion = () => {
    if (!selectedAnswer) return;
    
    // Only check paywall for non-premium users
    if (hasHitPaywall && !_userData?.isPremium) {
      onShowPaywall();
      return;
    }
    
    if (isLastQuestion) {
      onComplete(correctAnswers);
    } else {
      onNextQuestion();
      setSelectedAnswer(answeredQuestions[currentQuestionIndex + 1] || null);
      setShowAnswer(!!answeredQuestions[currentQuestionIndex + 1]);
    }
  };

  // Update useEffect to handle loading saved answers when moving between questions
  useEffect(() => {
    const savedAnswer = answeredQuestions[currentQuestionIndex];
    setSelectedAnswer(savedAnswer || null);
    setShowAnswer(!!savedAnswer);
  }, [currentQuestionIndex, answeredQuestions]);

  return (
    <div className="w-full max-w-4xl mx-auto px-3 sm:px-0 space-y-4 sm:space-y-8">
      <div className="bg-white p-4 sm:p-8 rounded-xl sm:rounded-3xl border border-gray-200 shadow-sm">
        <div className="space-y-3 sm:space-y-6">
          {/* Question Progress - Made more compact on mobile */}
          <div className="flex justify-between items-center text-xs sm:text-sm text-gray-500 font-medium">
            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
          </div>

          {/* Question Content - Better image handling */}
          <div className="space-y-3 sm:space-y-4">
            {currentQuestion.imageUrl && (
              <div className="relative w-full bg-gray-50 rounded-lg flex justify-center">
                <img 
                  src={currentQuestion.imageUrl} 
                  alt="Question illustration"
                  className="max-w-full w-auto max-h-[70vh] object-contain p-4"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <p className="text-sm sm:text-lg font-medium leading-relaxed">
              {currentQuestion.question}
            </p>
          </div>

          {/* Options - Improved touch targets and spacing */}
          <div className="space-y-2 sm:space-y-3">
            {currentQuestion.options.map((option, index) => {
              const optionLetter = String.fromCharCode(65 + index);
              const isAnswered = answeredQuestions[currentQuestionIndex];
              const correctAnswer = currentQuestion.correctOption || currentQuestion.answer;
              return (
                <button
                  key={index}
                  onClick={() => handleOptionClick(index)}
                  disabled={!!isAnswered}
                  className={`w-full min-h-[48px] p-3 sm:p-4 rounded-lg border text-left
                    text-sm sm:text-base transition-colors
                    ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer active:scale-[0.99]'}
                    ${selectedAnswer === optionLetter
                      ? optionLetter === correctAnswer
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : showAnswer && optionLetter === correctAnswer
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation - Better touch targets */}
      <div className="flex justify-between gap-3">
        <Button
          onClick={onPreviousQuestion}
          disabled={isFirstQuestion}
          variant="outline"
          className="min-h-[44px] px-4 sm:px-6 text-sm sm:text-base flex-1 sm:flex-none"
        >
          Previous
        </Button>
        <Button
          onClick={handleNextQuestion}
          disabled={!selectedAnswer}
          className="min-h-[44px] px-4 sm:px-6 text-sm sm:text-base flex-1 sm:flex-none"
        >
          {isLastQuestion ? 'Complete' : 'Next'}
        </Button>
      </div>

      {/* Answer Feedback - More compact on mobile */}
      {showAnswer && (
        <div className={`p-3 sm:p-4 rounded-lg ${
          selectedAnswer === (currentQuestion.correctOption || currentQuestion.answer)
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <p className="font-medium text-sm sm:text-base">
            {selectedAnswer === (currentQuestion.correctOption || currentQuestion.answer) 
              ? 'Correct!' 
              : 'Incorrect!'
            }
          </p>
          <p className="text-xs sm:text-sm mt-1">
            Correct Answer: {currentQuestion.options[
              (currentQuestion.correctOption || currentQuestion.answer).charCodeAt(0) - 65
            ]}
          </p>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(StudyMode); 